import { Button } from '@/components/common/Button';
import ButtonWithIcon from '@/components/common/ButtonWithIcon';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { PlusCircle } from '@/components/common/icons';
import Modal from '@/components/common/modals/Modal';
import { Paragraph, Text } from '@/components/common/Typography';
import AccountLockup from '@/components/financialEntities/AccountLockup';
import {
    hideFinancialEntityAddFlow,
    startFinancialEntityAddFlow,
} from '@/components/financialEntities/FinancialEntityAddFlow';
import ModalManager, { useModal } from '@/components/ModalManager';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { useMutation } from '@apollo/client';
import { Checkbox, Spacer } from '@chakra-ui/react';
import { SetRetirementGoalAssetsDocument } from '@common/graphql/generated';
import { sortBy } from '@common/util/arrays';
import { ComponentProps, useState } from 'react';
import {
    getMyIncludableAccounts,
    useAssets,
    useOnCompleteHandlers,
} from './helpers';
import { useRetirementGoalContext } from './RetirementGoalDetailContainer';

export interface IncludedAccountsModalProps extends Record<string, unknown> {}

export const RETIREMENT_GOAL_INCLUDED_ACCOUNTS_MODAL_ID =
    'retirement-goal-included-accounts-modal';

export const showIncludedAccountsModal = (props: IncludedAccountsModalProps) =>
    ModalManager.show(RETIREMENT_GOAL_INCLUDED_ACCOUNTS_MODAL_ID, props);

const IncludedAccountsModal = ModalManager.create<IncludedAccountsModalProps>(
    () => {
        useTrackModalShown('Retirement Goal Included Accounts');
        const modal = useModal();
        const { isSharedGoal, goal } = useRetirementGoalContext();
        const assets = useAssets();
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [setRetirementGoalAssets] = useMutation(
            SetRetirementGoalAssetsDocument
        );
        const { onCompleteConnection, onCompleteManual } =
            useOnCompleteHandlers();
        const myIncludableAccounts = sortBy(
            getMyIncludableAccounts(assets),
            // Sort RetirementAccount FEs first, then by institution, then by name
            (a) =>
                [
                    a.info.feType === 'RetirementAccount' ? 0 : 1,
                    a.institution?.name,
                    a.name,
                ].join('|')
        );
        const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>(
            myIncludableAccounts
                .filter((fe) => fe.goalId === goal.id)
                .map((fe) => fe.id)
        );

        const modalProps: ComponentProps<typeof Modal> = {
            size: 'md',
            isCentered: true,
            isOpen: modal.visible,
            onClose: modal.remove,
        };

        return (
            <Modal
                {...modalProps}
                title="Which accounts should be included in your retirement total?"
                renderFooter={() => (
                    <FlexRow
                        justifyContent="space-between"
                        alignItems="center"
                        p={5}
                        pl={8}
                    >
                        <ButtonWithIcon
                            variant="pill"
                            colorScheme="white"
                            iconLeft={<PlusCircle color="text" />}
                            h={9}
                            size="sm"
                            fontSize="md"
                            onClick={() => {
                                // Hide this modal
                                modal.remove();
                                // Open the "Add account" flow
                                startFinancialEntityAddFlow({
                                    isInRetirementContext: true,
                                    onBackFirstStep: () => {
                                        // Hide the other modal and return to this one
                                        hideFinancialEntityAddFlow();
                                        showIncludedAccountsModal({});
                                    },
                                    onCompleteConnection,
                                    onCompleteManual,
                                });
                            }}
                            onClickEvent="Add Retirement Account Button Clicked"
                            onClickEventParams={{ location: 'Retirement Goal' }}
                        >
                            Add a new account
                        </ButtonWithIcon>
                        <Button
                            isLoading={isSubmitting}
                            isDisabled={isSubmitting}
                            onClick={() => {
                                setIsSubmitting(true);
                                setRetirementGoalAssets({
                                    variables: {
                                        goalId: goal.id,
                                        financialEntityIds: selectedAccountIds,
                                    },
                                })
                                    .then(() => {
                                        modal.remove();
                                    })
                                    .finally(() => {
                                        setIsSubmitting(false);
                                    });
                            }}
                            onClickEvent="Confirm Retirement Accounts Button Clicked"
                            onClickEventParams={{ location: 'Retirement Goal' }}
                        >
                            Looks good
                        </Button>
                    </FlexRow>
                )}
            >
                <FlexCol>
                    {myIncludableAccounts.length === 0 ? (
                        <Text fontSize="sm">
                            You haven’t added any accounts yet
                        </Text>
                    ) : (
                        <FlexCol>
                            {isSharedGoal && (
                                <Paragraph color="textMuted" mb={8}>
                                    Any selected accounts{' '}
                                    <Text
                                        as="span"
                                        fontWeight="semibold"
                                        color="text"
                                    >
                                        will become shared with your partner
                                    </Text>
                                    , because your retirement goal is set to
                                    ‘Shared’.
                                </Paragraph>
                            )}
                            {myIncludableAccounts.map((account, idx) => (
                                <FlexRow
                                    key={account.id}
                                    py={4}
                                    borderTopWidth={idx === 0 ? 0 : 1}
                                    borderColor="border"
                                    data-cy="retirement-account-option"
                                >
                                    <AccountLockup account={account} />
                                    <Spacer />
                                    <Checkbox
                                        size="lg"
                                        isChecked={selectedAccountIds.includes(
                                            account.id
                                        )}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                // add
                                                setSelectedAccountIds([
                                                    ...selectedAccountIds,
                                                    account.id,
                                                ]);
                                            } else {
                                                // remove
                                                setSelectedAccountIds(
                                                    selectedAccountIds.filter(
                                                        (id) =>
                                                            id !== account.id
                                                    )
                                                );
                                            }
                                        }}
                                    />
                                </FlexRow>
                            ))}
                        </FlexCol>
                    )}
                </FlexCol>
            </Modal>
        );
    }
);

export default IncludedAccountsModal;
